import { inject, Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { ANetPayload } from "./authorize.types";
import { NotificationService } from "../../notification/notification.service";
import { LoadingService } from "../../loading.service";
import { AuthorizeLoaderService } from "./authorize-loader.service";

declare global {
  interface Window {
    Accept: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AuthorizeService {

  #notificationService = inject(NotificationService);
  #loadingService = inject(LoadingService);
  #authorizeLoaderService = inject(AuthorizeLoaderService);

  #APILoginID = environment.apis.authorize.APILoginID;
  #PulicClientKey = environment.apis.authorize.PublicClientkey;

  /**
   * Warms Accept.js without tokenising anything, for a form that is about to be filled in: the
   * download and the loader's settling wait then happen while the customer types rather than
   * after they press the button. Never rejects — a failure here is not the customer's problem
   * yet, and `sendPaymentDataToAnet` reports it if they do go on to pay.
   */
  preload(): Promise<void> {
    return this.#authorizeLoaderService.loadScript().catch(() => undefined);
  }

  /**
   * `errorHandler` runs on every path where `responseHandler` never will — the script failing
   * to load, Accept refusing the card, the browser being on plain HTTP. Without it a caller
   * that locked something before dispatching (a submit button, a spinner) stays locked, since
   * the failures below are reported by a toast and nothing else.
   */
  async sendPaymentDataToAnet(cardData: ANetPayload, responseHandler: Function, errorHandler?: Function) {
    const authData = {
      clientKey: this.#PulicClientKey,
      apiLoginID: this.#APILoginID
    };

    const secureData = {
      authData,
      cardData
    };

    this.#loadingService.open(cardData.cardNumber);

    // Every card in the app is tokenised here, so this is where the script gets waited on.
    // Accept.js defines `window.Accept` before its internal channel is up: dispatching in that
    // window fails with a code and no visible error, which reads as a backend fault. The loader
    // holds until it is genuinely ready, and fetches it from the host that matches the
    // credentials for this environment.
    try {
      await this.#authorizeLoaderService.loadScript();
    } catch {
      this.#loadingService.close(cardData.cardNumber);
      this.#notificationService.show({ text: 'We could not reach the payment service. Please try again.', type: 'warning' });
      errorHandler?.();
      return;
    }

    // The promise can resolve with Accept still unusable — a blocked request, a cached
    // failure — and `dispatchData` would then throw out here, outside the callback's own try.
    if (!window.Accept?.dispatchData) {
      this.#loadingService.close(cardData.cardNumber);
      this.#notificationService.show({ text: 'We could not reach the payment service. Please try again.', type: 'warning' });
      errorHandler?.();
      return;
    }

    window.Accept.dispatchData(secureData, (response: any) => {
      try {
        if (response.messages.resultCode === "Error") {
          this.#loadingService.close(cardData.cardNumber);
          var i = 0;
          while (i < response.messages.message.length) {
            console.log(
              response.messages.message[i].code + ": " +
              response.messages.message[i].text
            );
            this.#notificationService.show({ text: response.messages.message[i].text, type: 'warning' });
            i = i + 1;
          }
        } else {
          this.#loadingService.close(cardData.cardNumber);
          responseHandler(response.opaqueData);
        }
      } catch (error) {
        this.#loadingService.close(cardData.cardNumber);
        console.log('ANet Error >>', error);
      }
    });
  }

}
